import React from 'react';
import Toggle from 'react-native-toggle-element';
import { ViewStyle } from 'react-native';

export interface TrackBar {
    /**
     * The border width of the track bar.
     */
    borderWidth?: number;

    /**
     * The width and height of the track bar.
     */
    width?: number;

    /**
     * The height of the track bar.
     */
    height?: number;

    /**
     * The radius of the track bar.
     */
    radius?: number;

    /**
     * The background color of the track bar when the status
     * is active.
     */
    activeBackgroundColor?: string;

    /**
     * The background color of the track bar when the status
     * is inactive.
     */
    inActiveBackgroundColor?: string;

    /**
     * The border color of the track bar when the status
     * is active.
     */
    borderActiveColor?: string;

    /**
     * The border color of the track bar when the status
     * is inactive.
     */
    borderInActiveColor?: string;
}

export interface ThumbButton {
    /**
     * The border width of the thumb button.
     */
    borderWidth?: number;

    /**
     * The width of the thumb button.
     */
    width?: number;

    /**
     * The height of the thumb button.
     */
    height?: number;

    /**
     * The radius of the thumb button.
     */
    radius?: number;

    /**
     * The background color of the thumb button when the status
     * is active.
     */
    activeBackgroundColor?: string;

    /**
     * The background color of the thumb button when the status
     * is inactive.
     */
    inActiveBackgroundColor?: string;

    /**
     * The text color when the status is active.
     */
    activeColor?: string;

    /**
     * The text color when the status is inactive.
     */
    inActiveColor?: string;
}

export type MB_ToggleSwitchProps = {
    /**
     * The value of the toggle switch.
     */
    isToggled: boolean,

    /**
     * Callback called when the user toggles the switch.
     */
    onToggle: (newValue: boolean) => void,

    /**
     * The thumb button component when the status is active.
     * 
     * The thumb button is the button that the user slides to
     * toggle the switch.
     */
    thumbActiveComponent?: JSX.Element

    /**
     * The thumb button component when the status is inactive.
     * 
     * The thumb button is the button that the user slides to
     * toggle the switch.
     */
    thumbInActiveComponent?: JSX.Element,

    /**
     * Element to display on the left.
     */
    leftComponent?: JSX.Element,

    /**
     * Element to display on the right.
     */
    rightComponent?: JSX.Element,

    /**
     * The track bar component.
     * 
     * The track bar is the bar that the thumb button slides on.
     */
    trackBar?: TrackBar,

    /**
     * The thumb button component.
     * 
     * The thumb button is the button that the user slides to
     * toggle the switch.
     */
    thumbButton?: ThumbButton,

    /**
     * The style of the thumb button.
     */
    thumbStyle?: ViewStyle,

    /**
     * The style of the track bar.
     */
    trackBarStyle?: ViewStyle,

    /**
     * The title displayed on the left of the track bar.
     */
    leftTitle?: string,

    /**
     * The title displayed on the right of the track bar.
     */
    rightTitle?: string,

    /**
     * The duration of the toggle switch's animation.
     * 
     * Default: 200
     */
    animationDuration?: number
}

const MB_ToggleSwitch = ({
    isToggled,
    onToggle,
    thumbActiveComponent,
    thumbInActiveComponent,
    leftComponent,
    rightComponent,
    trackBar,
    thumbButton,
    thumbStyle,
    trackBarStyle,
    leftTitle,
    rightTitle,
    animationDuration = 200,
}: MB_ToggleSwitchProps) => {
    return (
        <Toggle
            value={isToggled}
            onPress={(newState) => onToggle(newState ?? false)}
            thumbActiveComponent={thumbActiveComponent}
            thumbInActiveComponent={thumbInActiveComponent}
            trackBar={trackBar}
            animationDuration={animationDuration}
            thumbButton={thumbButton}
            thumbStyle={thumbStyle}
            trackBarStyle={trackBarStyle}
            leftTitle={leftTitle}
            rightTitle={rightTitle}
            leftComponent={leftComponent}
            rightComponent={rightComponent}
        />
    );
};

export { MB_ToggleSwitch };
